import * as THREE from 'three';
import type { Point, UnitDefinition, UnitState } from '../../../shared/game/world-types.js';
import { authoredSoloClip, type AuthoredSoloClip } from '../../../shared/game/solo-clips.js';
import type { CharacterAction } from './character-animator.js';
import type { HitImpact } from './character-hit.js';
import type { GameplayUnitAnimator } from './unit-animator.js';
import { sampleTrack } from './authored-pair.js';
import { createShadowModel, type ShadowRig } from './shadow-model.js';

/** A called Shadow's pool opens first; the Shadow rises out of it over `RISE_SECONDS`. */
const POOL_LEAD = .25;
const RISE_SECONDS = .95;
/** Seconds it takes to come apart when it falls or its fight ends (the server keeps it a little longer, `ALLY_FADE_SECONDS`). */
const DISSOLVE_SECONDS = 2.1;
const RECOIL_SECONDS = .55;
/** The Shadow's clips, made in Blender (`npm run anim -- open shadow-idle`; docs/animation-authoring.md). */
const CLIPS = { idle: 'shadow-idle', reach: 'shadow-reach', slither: 'shadow-slither', gather: 'shadow-swipe-1', blow: 'shadow-swipe-2' } as const;
export { CLIPS as SHADOW_CLIPS };
/** Metres glided per cycle of the slither clip: one slow S of the body. */
export const SLITHER_METRES = 3.4;
/** How far apart the slow reaches toward the watched foe come, and how long the blend into and out of a reach and out of the blow takes. */
const REACH_EVERY = [7, 13] as const, REACH_BLEND = .6, BLOW_BLEND_OUT = .3;
const ease = (p: number) => 1 - (1 - p) ** 3;
const smooth = THREE.MathUtils.smoothstep;

interface Layer { clip: AuthoredSoloClip | undefined; time: number; weight: number }

/**
 * A Shadow's performance. Its motion is five clips made in Blender, blended on its skeleton: it floats and stalks its foe (`shadow-idle`), now
 * and then reaches slowly toward it (`shadow-reach`), slithers low as it glides, paced by the ground covered (`shadow-slither`), sweeps one claw
 * across while it gathers a blow (`shadow-swipe-1`, stretched to the windup) and lands it with the other (`shadow-swipe-2`). The code does the
 * rest: turning to its foe, rising out of its pool, the jolt of a blow, coming apart into smoke, and the smoke and eyes over all of it. It never
 * touches the ground, so it has no footfalls, no ragdoll and no Prone fall: its death is the dissolve, not the shared death physics.
 */
export class ShadowAnimator implements GameplayUnitAnimator {
  readonly rig: ShadowRig;
  onStep: (() => void) | null = null;
  groundHeight?: (x: number, z: number) => number;
  readonly proneState = 'standing' as const;
  private time?: number;
  private previous?: Point;
  private facing = 0;
  private glide = 0;
  private seed: number;
  private riseAt?: number;
  private deathAt?: number;
  private strikeAt?: number;
  private recoil?: { at: number; away: THREE.Vector2; strength: number };
  private windup = 0;
  /** When the blow being gathered began, on this clock: the snapshot's remaining time arrives in steps, so progress is read off the frame time. */
  private windupStart?: number;
  private menace = 0;
  /** Cycles of the slither clip played, advanced by the distance glided. */
  private slither = 0;
  /** The slow reach toward the watched foe now under way, and when the next may begin. */
  private reachAt?: number;
  private nextReach?: number;
  private reaches = 0;
  private clips = new Map<string, AuthoredSoloClip>();
  private rest = new Map<string, THREE.Vector3>();
  private turn = new THREE.Quaternion();
  private place = new THREE.Vector3();
  private disposed = false;
  constructor(private definition: UnitDefinition) {
    this.rig = createShadowModel(definition);
    for (const [name, bone] of this.rig.joints) this.rest.set(name, bone.position.clone());
    let seed = 0; for (const char of definition.id) seed = (seed * 31 + char.charCodeAt(0)) % 1000; this.seed = seed / 1000;
  }
  get deathState(): 'alive' | 'falling' | 'settled' {
    if (this.deathAt === undefined || this.time === undefined) return 'alive';
    return this.time - this.deathAt < DISSOLVE_SECONDS ? 'falling' : 'settled';
  }
  get settledCorpse() { return this.deathState === 'settled'; }
  reset() {
    this.time = undefined; this.previous = undefined; this.riseAt = undefined; this.deathAt = undefined; this.strikeAt = undefined; this.recoil = undefined;
    this.windup = 0; this.windupStart = undefined; this.menace = 0; this.glide = 0; this.reachAt = undefined; this.nextReach = undefined;
  }
  stopActions() { this.strikeAt = undefined; this.recoil = undefined; }
  play(kind: CharacterAction, at: number, toward?: Point, _fromProgress = 0, impact?: HitImpact) {
    if (this.disposed) return;
    // The blow's cue arrives about when the windup ends, which has already started the second swipe.
    if (kind === 'slash' && (this.strikeAt === undefined || Math.abs(at - this.strikeAt) > .4)) this.strikeAt = at;
    if (kind === 'hit' || kind === 'block') {
      const from = toward && this.previous ? new THREE.Vector2(this.previous.x - toward.x, this.previous.z - toward.z) : new THREE.Vector2(0, -1);
      if (from.lengthSq() < 1e-6) from.set(0, -1);
      this.recoil = { at, away: from.normalize(), strength: kind === 'block' ? .5 : THREE.MathUtils.clamp((impact?.damage ?? 4) / 6, .6, 1.4) };
    }
  }
  playing() {
    if (this.strikeAt === undefined || this.time === undefined) return undefined;
    const progress = (this.time - this.strikeAt) / this.strikeSeconds;
    return progress >= 0 && progress < 1 ? { kind: 'slash' as CharacterAction, progress } : undefined;
  }
  /**
   * While a blow is gathered and landed: how far into the blow it is (0 while gathering) and the tips of both hands' middle claws in the world,
   * which drag a trail of smoke through the air. The swing starts in the gathering clip, so the trail does too.
   */
  clawTips(): { progress: number; tips: THREE.Vector3[] } | undefined {
    const blow = this.playing() ?? (this.windup > 0 ? { progress: 0 } : undefined);
    if (!blow || this.disposed || this.deathAt !== undefined) return undefined;
    this.rig.root.updateWorldMatrix(true, true);
    return { progress: blow.progress, tips: this.rig.arms.map(arm => arm.hand.fingers[1].at(-1)!.localToWorld(new THREE.Vector3(0, -.07, 0))) };
  }
  /** Starts a slow reach toward the watched foe at `at` instead of waiting for the next one, so the explorer can show it on demand. */
  reachNow(at: number) { this.reachAt = at; this.nextReach = at + (this.clip(CLIPS.reach)?.seconds ?? 4) + REACH_EVERY[0]; }
  /** The blow lands at the end of the windup, so a cue that arrives without one starts the second swipe at once. */
  swingLead() { return 0; }
  private get strikeSeconds() { return this.clip(CLIPS.blow)?.seconds ?? 1; }
  /** A clip by id, once the clip library has loaded; until then the figure keeps its rest pose. */
  private clip(id: string) {
    let clip = this.clips.get(id);
    if (!clip && (clip = authoredSoloClip(id))) this.clips.set(id, clip);
    return clip;
  }
  update(state: UnitState, position: Point, gameTime: number, target?: Point) {
    if (this.disposed) return;
    if (this.time !== undefined && gameTime < this.time) this.reset();
    if (gameTime === this.time) return;
    const first = this.time === undefined, elapsed = first ? 0 : gameTime - this.time!, dt = Math.min(.25, elapsed);
    if (first) {
      // Called just now: it rises. Already standing when first seen (a reconnect, a reload): it simply floats there.
      if (!state.health.dead) this.riseAt = gameTime;
      else this.deathAt = gameTime - DISSOLVE_SECONDS;
    }
    if (state.health.dead && this.deathAt === undefined) this.deathAt = gameTime;
    if (!state.health.dead && this.deathAt !== undefined && !first) this.deathAt = undefined;
    const { body } = this.rig;
    const dx = this.previous ? position.x - this.previous.x : 0, dz = this.previous ? position.z - this.previous.z : 0, moved = Math.hypot(dx, dz);
    const moving = elapsed > 0 && moved > 1e-4 && moved < Math.max(10, this.definition.speed * 2.5) * elapsed && !state.health.dead;
    const speed = moving ? moved / elapsed : 0;
    this.glide = THREE.MathUtils.damp(this.glide, THREE.MathUtils.clamp(speed / this.definition.speed, 0, 1), 3, dt);
    this.slither += moving ? moved / SLITHER_METRES : dt * .08 * this.glide;
    const aim = target ? { x: target.x - position.x, z: target.z - position.z } : moving ? { x: dx, z: dz } : undefined;
    if (aim && Math.hypot(aim.x, aim.z) > .001 && !state.health.dead) {
      const desired = Math.atan2(aim.x, aim.z), delta = THREE.MathUtils.euclideanModulo(desired - this.facing + Math.PI, Math.PI * 2) - Math.PI;
      this.facing += delta * (first ? 1 : 1 - Math.exp(-4 * dt));
    }
    this.rig.root.rotation.y = this.facing;

    const gathering = state.combat.windup?.kind === 'attack' && !state.health.dead ? state.combat.windup : undefined;
    if (gathering) {
      const start = gameTime - (gathering.duration - gathering.remaining);
      if (this.windupStart === undefined || Math.abs(start - this.windupStart) > .25) this.windupStart = start;
    } else this.windupStart = undefined;
    const windup = gathering ? THREE.MathUtils.clamp((gameTime - this.windupStart!) / Math.max(.01, gathering.duration), 0, .999) : 0;
    // The blow lands as the windup ends: a windup that just finished is the second swipe.
    if (this.windup > .5 && windup === 0 && !state.health.dead) this.strikeAt = gameTime;
    this.windup = windup;
    const t = gameTime + this.seed * 13, k = this.seed * 10;
    // Coming apart, the clips stop where they were.
    const deadP = this.deathAt === undefined ? 0 : THREE.MathUtils.clamp((gameTime - this.deathAt) / DISSOLVE_SECONDS, 0, 1);
    const clock = this.deathAt === undefined ? t : this.deathAt + this.seed * 13;

    const strikeP = this.strikeAt === undefined ? 1 : (gameTime - this.strikeAt) / this.strikeSeconds, striking = strikeP >= 0 && strikeP < 1;
    const gather = windup > 0 ? smooth(windup, 0, .12) : 0, blow = striking ? smooth((1 - strikeP) * this.strikeSeconds, 0, BLOW_BLEND_OUT) : 0;
    const busy = Math.max(gather, blow);
    // With a foe to watch it stalks it, and now and then reaches slowly toward it.
    this.menace = THREE.MathUtils.damp(this.menace, target && !state.health.dead ? 1 : 0, 1.5, dt);
    const watch = this.menace * (1 - this.glide) * (1 - busy);
    const reachClip = this.clip(CLIPS.reach), reachSeconds = reachClip?.seconds ?? 4;
    if (watch > .6 && this.reachAt === undefined && (this.nextReach === undefined || gameTime >= this.nextReach)) {
      if (this.nextReach !== undefined) this.reachAt = gameTime;
      const gap = REACH_EVERY[0] + (REACH_EVERY[1] - REACH_EVERY[0]) * (.5 + .5 * Math.sin(++this.reaches * 2.39 + k));
      this.nextReach = gameTime + reachSeconds + gap;
    }
    const reachTime = this.reachAt === undefined ? reachSeconds : gameTime - this.reachAt;
    if (reachTime >= reachSeconds) this.reachAt = undefined;
    const reach = reachTime < reachSeconds ? smooth(reachTime, 0, REACH_BLEND) * smooth(reachSeconds - reachTime, 0, REACH_BLEND) * watch : 0;

    // The clips, each over the ones before it: stalking, gliding, reaching, gathering the blow and landing it.
    const idle = this.clip(CLIPS.idle), slither = this.clip(CLIPS.slither), gatherClip = this.clip(CLIPS.gather), blowClip = this.clip(CLIPS.blow);
    this.pose([
      { clip: idle, time: THREE.MathUtils.euclideanModulo(clock, idle?.seconds ?? 1), weight: 1 },
      { clip: slither, time: THREE.MathUtils.euclideanModulo(this.slither, 1) * (slither?.seconds ?? 1), weight: this.glide * (1 - busy) },
      { clip: reachClip, time: reachTime, weight: reach },
      { clip: gatherClip, time: windup * (gatherClip?.seconds ?? 1), weight: gather },
      { clip: blowClip, time: striking ? strikeP * this.strikeSeconds : 0, weight: blow },
    ]);

    const recoilP = this.recoil ? (gameTime - this.recoil.at) / RECOIL_SECONDS : 1, recoil = recoilP >= 0 && recoilP < 1 ? (1 - recoilP) ** 2 * this.recoil!.strength : 0;
    const shudder = recoil * Math.sin((gameTime - (this.recoil?.at ?? 0)) * 46);
    // Rising and dissolving.
    const called = this.riseAt === undefined ? Infinity : gameTime - this.riseAt;
    const riseP = THREE.MathUtils.clamp((called - POOL_LEAD) / RISE_SECONDS, 0, 1), risen = ease(riseP);
    // Recoil: away from the blow, in the figure's own frame, pivoting at the ground under its tail.
    const away = this.recoil ? this.recoil.away.clone().rotateAround(new THREE.Vector2(), this.facing) : new THREE.Vector2();
    body.position.set(shudder * .03, -(1 - risen) * 1.5 * (1 - deadP) - deadP * .55, 0);
    body.rotation.set(recoil * away.y * .2 + deadP * .35, 0, -recoil * away.x * .2 + shudder * .04);
    body.scale.set(1 + deadP * .25, Math.max(.05, (.55 + .45 * risen) * (1 - deadP * .8)), 1 + deadP * .25);

    // The smoke streams back off the glide and the lunge, and churns as it gathers itself and strikes.
    const { cloth, smoke } = this.rig, tail = Math.sin(this.slither * Math.PI * 2 - 1.3) * this.glide, drive = blow * (1 - smooth(strikeP, .3, .7));
    cloth.uTime.value = t;
    cloth.uStream.value.set(-tail * .8, -this.glide * 1.3 - drive * .5 + recoil * .4);
    cloth.uFlutter.value = 1 + this.glide * .8 + watch * .3 + reach * .6 + gather * .6 + recoil * 2 + deadP * 2;
    const fade = risen * (1 - smooth(deadP, .1, .95)) * (1 - recoil * .35 * (.5 + .5 * Math.sin((gameTime - (this.recoil?.at ?? 0)) * 30)));
    cloth.uFade.value = fade; cloth.uRim.value = 1 + recoil + reach * .6 + drive;
    smoke.uTime.value = t; smoke.uFade.value = fade; smoke.uDensity.value = 1 + this.glide * .2 + reach * .3 + gather * .3 + drive * .5 + recoil * .5;
    for (const material of this.rig.fading) material.opacity = fade;
    // The eyes burn brighter as it reaches and strikes, and flicker when it is struck.
    const eyes = fade * (.75 + .12 * Math.sin(t * 2.3) + reach * .5 + gather * .4 + drive) * (1 - recoil * .6 * (.5 + .5 * Math.sin(gameTime * 37)));
    for (const material of this.rig.glow) material.opacity = THREE.MathUtils.clamp(eyes, 0, 1.6);
    // The pool opens before the Shadow rises from it and outlasts it as it sinks back; smoke bursts off it at both.
    const shroud = this.rig.shroud.uniforms, burst = Math.exp(-Math.max(0, called - POOL_LEAD) * 2.2) * (called < Infinity ? 1 : 0);
    const pool = smooth(called, 0, POOL_LEAD + .2) * (1 + .3 * Math.sin(deadP * Math.PI)) * (1 - smooth(deadP, .55, 1));
    shroud.uTime.value = t;
    shroud.uPool.value = pool * (.9 + .06 * Math.sin(t * 1.3) + watch * .08 + reach * .15) + drive * .2;
    shroud.uWisps.value = deadP > 0 ? 2.2 * (1 - smooth(deadP, .5, 1)) : .8 + this.glide * .4 + watch * .25 + recoil * .8 + reach * .4 + gather * .3 + burst * 1.6;
    shroud.uLift.value = 1 + burst * 1.4 + deadP * 1.8;
    shroud.uStream.value.copy(cloth.uStream.value);
    const shown = fade > .01 || pool > .01;
    this.rig.root.visible = shown;
    body.visible = fade > .01;
    this.previous = { ...position }; this.time = gameTime;
  }
  /** Poses every joint from the clips, each blended over the ones before it by its weight. A joint a clip leaves out is at rest in it; of the joints' places only the hips' moves. */
  private pose(layers: Layer[]) {
    for (const [name, bone] of this.rig.joints) {
      const rest = this.rest.get(name)!;
      bone.quaternion.identity(); bone.position.copy(rest);
      for (const { clip, time, weight } of layers) {
        if (!clip || weight <= 0) continue;
        const track = clip.tracks.joints[name];
        if (track?.q) this.turn.fromArray(sampleTrack(track.q, 4, clip, time, true)); else this.turn.identity();
        bone.quaternion.slerp(this.turn, weight);
        if (track?.p) this.place.fromArray(sampleTrack(track.p, 3, clip, time)); else this.place.copy(rest);
        bone.position.lerp(this.place, weight);
      }
    }
  }
  dispose() {
    if (this.disposed) return; this.disposed = true;
    disposeTree(this.rig.root);
  }
}

function disposeTree(root: THREE.Object3D) {
  root.removeFromParent();
  const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>(), textures = new Set<THREE.Texture>();
  root.traverse(node => {
    if (node instanceof THREE.Mesh || node instanceof THREE.Points) { geometries.add(node.geometry); for (const material of [node.material].flat()) materials.add(material); }
    if (node instanceof THREE.Sprite) { materials.add(node.material); if (node.material.map) textures.add(node.material.map); }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
  for (const texture of textures) texture.dispose();
}
